// Corre la importación de una planilla en Tienda online, sin IA
// (ADR-2026-10-30-exportar-e-importar-en-su-pantalla). Lo que decidieron las casillas ya viene resuelto en
// `PlanillaParaImportar` (importarPlanilla.ts); acá se escribe:
//
//   · cada producto nuevo, con los datos de su primera fila, en el catálogo y en la lista de cambios de la
//     tienda (como "Agregar producto"): se crea en Tiendanube al sincronizar;
//   · cada uno que ya está, en su ficha (la misma de editarlo a mano), destrabando lo que se toca.
//
// Al terminar avisa en la campana (lo escribe el backend: el front no crea notificaciones). Nunca tira: lo
// que falla se cuenta.

import { atlasBackendFetch } from "../../lib/atlasBackend";
import { filasDePlanilla } from "./agenteLanzamiento";
import { avisarCambioDeLanzamiento } from "./cambioDeLanzamiento";
import { cambiosDeLaFicha } from "./edicionMasiva";
import type { ExistenteEnLaTienda, PlanillaParaImportar } from "./importarPlanilla";
import {
  actualizarFicha,
  actualizarVariante,
  fetchLanzamiento,
  guardarSeccionesDestrabadas,
  type CamposDeFicha,
} from "./lanzamientosApi";
import { crearProductoNuevo, filaDeProductoDeTiendanube, lanzamientoDeLaTienda, type ProductoDeLaTienda } from "./productosDeLaTienda";
import { conDestrabadas, seccionDeCampo, type SeccionTrabable } from "./seccionesTrabadas";

export interface ResultadoDeImportacion {
  creados: number;
  modificados: number;
  salteados: number;
  /** Una línea por producto que no se pudo. */
  fallas: string[];
  /** Lo que se hizo distinto de lo que decía la planilla, y por qué (no son errores). */
  notas: string[];
  lanzamientoId: string | null;
}

export type PasoDeImportacion = { paso: "preparando" } | { paso: "creando" | "modificando"; hechos: number; total: number };

type Campos = ReturnType<typeof filasDePlanilla>["productos"][number]["campos"];

/** Los datos de cada producto de la planilla (el encabezado + sus filas): los de la primera fila, con el stock sumado. */
export function camposPorProducto(filas: string[][], grupos: number[]): Campos[] {
  const [encabezado = [], ...cuerpo] = filas;
  const out: Campos[] = [];
  let i = 0;
  for (const n of grupos) {
    const suyas = cuerpo.slice(i, i + n);
    i += n;
    const leidas = filasDePlanilla([encabezado, ...suyas]).productos;
    const campos: Campos = { ...(filasDePlanilla([encabezado, suyas[0] ?? []]).productos[0]?.campos ?? {}) };
    // Las variantes de abajo suman su stock al del producto.
    const stocks = leidas.map((p) => p.campos.stock).filter((s): s is string => !!s);
    if (stocks.length > 1) campos.stock = String(stocks.reduce((t, s) => t + (Number(s.replace(",", ".")) || 0), 0));
    out.push(campos);
  }
  return out;
}

const normal = (s: string) =>
  s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

/** Las filas de cada producto (sin el encabezado), según cuántas ocupa cada uno. */
function filasDeCadaProducto(filas: string[][], grupos: number[]): string[][][] {
  const cuerpo = filas.slice(1);
  let i = 0;
  return grupos.map((n) => {
    const suyas = cuerpo.slice(i, i + n);
    i += n;
    return suyas;
  });
}

/**
 * El stock y el precio de cada variante de un producto que ya está, desde sus filas de la planilla (formato
 * Tiendanube: una fila por variante). Cada fila se ata a su variante por SKU o, si no, por sus valores
 * ("Blanco", "Large"). Las que no se encuentran no se tocan. Puro.
 */
export function datosDeLasVariantes(
  encabezado: string[],
  filas: string[][],
  variantes: ReadonlyArray<{ id: string; sku: string | null; valores: string[] }>,
): Map<string, { stock?: number; precio?: number }> {
  const col = (re: RegExp) => encabezado.findIndex((h) => re.test(normal(h)));
  const iStock = col(/^(stock|cantidad|inventario)$/);
  const iPrecio = col(/^precio$/);
  const iSku = col(/^sku$/);
  const iValores = encabezado.flatMap((h, i) => (/^valor de propiedad \d$/.test(normal(h)) ? [i] : []));
  const out = new Map<string, { stock?: number; precio?: number }>();
  for (const fila of filas) {
    const sku = iSku >= 0 ? normal(fila[iSku] ?? "") : "";
    const valores = iValores.map((i) => normal(fila[i] ?? "")).filter(Boolean).sort().join("|");
    const v =
      (sku && variantes.find((x) => x.sku && normal(x.sku) === sku)) ||
      (valores && variantes.find((x) => x.valores.map(normal).filter(Boolean).sort().join("|") === valores)) ||
      null;
    if (!v) continue;
    const stock = iStock >= 0 ? numero(fila[iStock]) : null;
    const precio = iPrecio >= 0 ? numero(fila[iPrecio]) : null;
    const datos = {
      ...(stock !== null ? { stock: Math.max(0, Math.floor(stock)) } : {}),
      ...(precio !== null && precio > 0 ? { precio } : {}),
    };
    if (Object.keys(datos).length > 0) out.set(v.id, datos);
  }
  return out;
}

/**
 * Lo que se escribe directo en la ficha (lo de texto y medidas); precio y stock van aparte. Cada campo destraba
 * SU sección (`seccionDeCampo`, la misma de la ficha): marca y etiquetas son de SEO, y destrabar otra los
 * guardaba sin mandarlos (review Greptile de #232).
 */
function camposDeTexto(c: Campos): { campos: CamposDeFicha; secciones: SeccionTrabable[] } {
  const campos: CamposDeFicha = {};
  const secciones = new Set<SeccionTrabable>();
  for (const k of ["nombre", "descripcion", "marca", "tags", "codigoBarras", "sku", "costo", "peso", "alto", "ancho", "profundidad"] as const) {
    if (!c[k]) continue;
    campos[k] = c[k];
    const seccion = seccionDeCampo(k);
    if (seccion) secciones.add(seccion);
  }
  return { campos, secciones: [...secciones] };
}

/** Un número escrito en la planilla. Sin dígitos ("Sin límite", "") no es un número: no es 0. */
const numero = (v: string | undefined) => {
  const limpio = (v ?? "").replace(/[^\d.,-]/g, "").replace(",", ".");
  if (!/\d/.test(limpio)) return null;
  const n = Number(limpio);
  return Number.isFinite(n) ? n : null;
};

export async function correrImportacion<T extends ExistenteEnLaTienda & ProductoDeLaTienda>(p: {
  plan: PlanillaParaImportar<T>;
  companyId: string;
  brandId: string | null;
  userId: string | undefined;
  archivo: string;
  alAvanzar?: (paso: PasoDeImportacion) => void;
}): Promise<ResultadoDeImportacion> {
  const r: ResultadoDeImportacion = {
    creados: 0,
    modificados: 0,
    salteados: p.plan.salteados,
    fallas: [],
    notas: p.plan.ambiguos.map((n) => `${n}: hay más de un producto con ese nombre en tu tienda; sumale su ID o su identificador de URL para importarlo`),
    lanzamientoId: null,
  };
  const destino = { userId: p.userId, companyId: p.companyId, brandId: p.brandId };
  p.alAvanzar?.({ paso: "preparando" });
  let lanzamientoId: string;
  try {
    lanzamientoId = await lanzamientoDeLaTienda({ ...destino, plataforma: "tiendanube" });
    r.lanzamientoId = lanzamientoId;
  } catch {
    r.fallas.push("No pudimos preparar tu tienda para importar");
    await avisar(p.archivo, r);
    return r;
  }

  // 1. Los nuevos.
  const nuevos = camposPorProducto(p.plan.nuevos, p.plan.filasPorNuevo);
  for (const [i, c] of nuevos.entries()) {
    p.alAvanzar?.({ paso: "creando", hechos: i, total: nuevos.length });
    const nombre = c.nombre ?? c.sku ?? `Producto ${i + 1}`;
    try {
      const { filaId } = await crearProductoNuevo(destino, nombre, lanzamientoId);
      const { campos } = camposDeTexto(c);
      if (c.precio) campos.precio = c.precio;
      const stock = numero(c.stock);
      if (stock !== null) campos.stock = Math.max(0, Math.floor(stock));
      const { error } = await actualizarFicha(filaId, campos);
      if (error) r.fallas.push(`${nombre}: se creó, pero no se guardaron sus datos`);
      r.creados++;
    } catch {
      r.fallas.push(`${nombre}: no se pudo crear`);
    }
  }

  // 2. Los que ya están: a su ficha.
  const datos = camposPorProducto(p.plan.aModificar, p.plan.filasPorExistente);
  const filasDeExistentes = filasDeCadaProducto(p.plan.aModificar, p.plan.filasPorExistente);
  for (const [i, producto] of p.plan.existentes.entries()) {
    p.alAvanzar?.({ paso: "modificando", hechos: i, total: p.plan.existentes.length });
    const c = datos[i] ?? {};
    try {
      const { filaId } = await filaDeProductoDeTiendanube(producto, destino);
      if (!filaId) throw new Error();
      const detalle = await fetchLanzamiento(lanzamientoId, p.companyId);
      const fila = detalle?.productos_.find((x) => x.id === filaId);
      if (!fila) throw new Error();
      const texto = camposDeTexto(c);
      const precio = numero(c.precio);
      const stock = numero(c.stock);
      const suyas = filasDeExistentes[i] ?? [];
      // Con variantes y una fila por variante, cada una lo suyo (por SKU o valores). Con variantes y UNA fila
      // (un resumen, como el de Exportar) el stock es la suma de todas: no se puede repartir y no se toca.
      const conVariantes = fila.variantes.length > 0;
      const porVariante = conVariantes && suyas.length > 1 ? datosDeLasVariantes(p.plan.aModificar[0] ?? [], suyas, fila.variantes) : null;
      const preciosDistintos =
        !!porVariante && new Set([...porVariante.values()].flatMap((d) => (d.precio !== undefined ? [d.precio] : []))).size > 1;
      if (conVariantes && !porVariante && stock !== null) {
        r.notas.push(`${producto.nombre}: tiene variantes y la planilla trae un solo stock; el stock no se cambió`);
      }
      const masiva = cambiosDeLaFicha(fila, {
        ...(precio !== null && precio > 0 && !preciosDistintos ? { precio: { modo: "fijar" as const, valor: precio } } : {}),
        ...(stock !== null && !conVariantes ? { stock } : {}),
        tiendas: { tiendanube: true, mercadolibre: false },
      });
      const campos: CamposDeFicha = { ...texto.campos, ...masiva.campos };
      const secciones = new Set([...texto.secciones, ...masiva.secciones]);
      if (porVariante) {
        for (const [id, d] of porVariante) {
          const ya = masiva.variantes.find((v) => v.id === id) ?? masiva.variantes[masiva.variantes.push({ id, campos: {} }) - 1]!;
          if (d.stock !== undefined) ya.campos.stock = String(d.stock);
          if (preciosDistintos && d.precio !== undefined) ya.campos.precio = String(d.precio);
        }
        if (porVariante.size > 0) secciones.add("variantes");
        // Precios distintos por variante: se prende el precio por variante (si no, quedaban todas con el primero).
        if (preciosDistintos) {
          campos.precioPorVariante = true;
          secciones.add("precio");
        }
      }
      if (Object.keys(campos).length > 0) {
        const { error } = await actualizarFicha(filaId, campos);
        if (error) throw new Error();
      }
      for (const v of masiva.variantes) {
        if ((await actualizarVariante(v.id, v.campos)).error) throw new Error();
      }
      if (secciones.size > 0 && (await guardarSeccionesDestrabadas(filaId, conDestrabadas(fila.seccionesDestrabadas, [...secciones]))).error) {
        throw new Error();
      }
      r.modificados++;
    } catch {
      r.fallas.push(`${producto.nombre}: no se pudo actualizar`);
    }
  }

  if (r.creados + r.modificados > 0) avisarCambioDeLanzamiento(lanzamientoId);
  await avisar(p.archivo, r);
  return r;
}

/** El aviso en la campana. Si no sale, la importación igual quedó hecha: no se reporta como falla. */
async function avisar(archivo: string, r: ResultadoDeImportacion) {
  try {
    await atlasBackendFetch("/v1/tienda/importar-planilla/aviso", {
      archivo,
      creados: r.creados,
      modificados: r.modificados,
      salteados: r.salteados,
      fallidos: r.fallas.length,
    });
  } catch {
    // Sin aviso: la pantalla ya mostró el resultado.
  }
}
